/**
 * Recent Plans Dropdown Component
 *
 * A compact dropdown menu for accessing recent plans from the header.
 */

import { useState, useEffect, useRef } from "react";
import { getRecentPlans, removeRecentPlan, type RecentPlan } from "../lib/storage";

function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d`;
  if (hours > 0) return `${hours}h`;
  if (minutes > 0) return `${minutes}m`;
  return "now";
}

export default function RecentPlansDropdown() {
  const [plans, setPlans] = useState<RecentPlan[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPlans(getRecentPlans());
  }, []);

  // Refresh list when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setPlans(getRecentPlans());
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleRemove = (e: React.MouseEvent, token: string) => {
    e.preventDefault();
    e.stopPropagation();
    removeRecentPlan(token);
    setPlans(getRecentPlans());
  };

  if (plans.length === 0) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className="btn btn-ghost btn-sm"
        onClick={() => setIsOpen(!isOpen)}
        title="Recent plans"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6h16M4 10h16M4 14h16M4 18h16"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-base-100 border border-base-300 rounded-lg shadow-lg z-50">
          <div className="px-3 py-2 border-b border-base-300">
            <span className="text-sm font-medium">Recent Plans</span>
          </div>
          <ul className="max-h-80 overflow-auto py-1">
            {plans.map((plan) => {
              const displayName = plan.petname || plan.name || `${plan.token.slice(0, 8)}...`;
              return (
                <li key={plan.token}>
                  <a
                    href={`/p/${plan.token}`}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-base-200 group"
                  >
                    <span className="flex-1 truncate text-sm">
                      {displayName}
                    </span>
                    <span className={`badge badge-xs ${
                      plan.scope === "admin" ? "badge-success" :
                      plan.scope === "append" ? "badge-info" :
                      "badge-warning"
                    }`}>
                      {plan.scope}
                    </span>
                    <span className="text-xs text-base-content/50">
                      {formatRelativeTime(plan.lastAccessed)}
                    </span>
                    <button
                      className="btn btn-ghost btn-xs opacity-0 group-hover:opacity-100"
                      onClick={(e) => handleRemove(e, plan.token)}
                      title="Remove"
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
